/**
 * MonitoredProductsTabContent Component
 *
 * Content for the Monitored Products tab in the dashboard.
 * Displays filtered products with pagination and empty state handling.
 */

import { PackageIcon, PlusIcon } from '@shopify/polaris-icons';
import { TabEmptyState } from './TabEmptyState';
import { MonitoredProductsTable } from './MonitoredProductsTable';
import type { TrackedProduct, OnboardingData } from '../../types/dashboard';

/**
 * Props for MonitoredProductsTabContent component
 */
export interface MonitoredProductsTabContentProps {
  /** Filtered products to display */
  filteredProducts: TrackedProduct[];
  /** Current search query */
  searchQuery: string;
  /** Onboarding data for product setup (can be null if not loaded) */
  onboardingData: OnboardingData | null;
  /** Shop domain for product links */
  shopDomain: string;
  /** Handler for product click to open settings modal */
  onProductClick: (product: TrackedProduct) => void;
  /** Whether the shop has test data */
  hasTestData?: boolean;
  /** Current page number (1-indexed) */
  currentPage: number;
  /** Total number of pages */
  totalPages: number;
  /** Number of items per page */
  itemsPerPage: number;
  /** Handler for page change */
  onPageChange: (page: number) => void;
  /** Handler to open the onboarding wizard */
  onOpenWizard: () => void;
}

/**
 * Tab content component for the Monitored Products tab
 */
export function MonitoredProductsTabContent({
  filteredProducts,
  searchQuery,
  onboardingData,
  shopDomain,
  onProductClick,
  hasTestData,
  currentPage,
  totalPages,
  itemsPerPage,
  onPageChange,
  onOpenWizard,
}: MonitoredProductsTabContentProps) {
  if (filteredProducts.length === 0) {
    return (
      <div className="empty-state-section">
        <TabEmptyState
          icon={searchQuery ? PackageIcon : undefined}
          illustration={searchQuery ? undefined : 'no-monitored-products'}
          title="No products monitored"
          description={
            searchQuery
              ? `No products match "${searchQuery}"`
              : 'Start monitoring products to track their inventory levels'
          }
          action={
            !searchQuery
              ? {
                  content: 'Setup wizard',
                  onClick: onOpenWizard,
                  variant: 'primary',
                  icon: PlusIcon,
                }
              : undefined
          }
        />
      </div>
    );
  }

  return (
    <MonitoredProductsTable
      products={filteredProducts}
      onboardingData={onboardingData}
      shopDomain={shopDomain}
      onProductClick={onProductClick}
      hasTestData={hasTestData}
      currentPage={currentPage}
      totalPages={totalPages}
      itemsPerPage={itemsPerPage}
      onPageChange={onPageChange}
    />
  );
}
